// Shared helpers for evidence / external links — the single source of truth
// for the default first-link label ("CoC"), http(s) URL safety, the render-
// sink href guard, and the save-time clean/drop/validate rule. The insurance
// form, every detail-expand render sink, and their tests all import from here
// so the seed label and the drop rule can never drift apart, and so the
// stored-XSS mitigation is uniform across every surface.

import type { EvidenceLink } from "../types";

/** Default label seeded into the first evidence link of an insurance record
 *  (Certificate of Currency). Used for both the form seed and the drop rule
 *  so an untouched seed row is recognised and silently discarded. */
export const DEFAULT_EVIDENCE_LABEL = "CoC";

const EVIDENCE_LINK_ERROR =
  "Each evidence link needs a label and an http(s) URL.";

/**
 * True only for http(s) URLs. `new URL("javascript:…")` and `new URL("data:…")`
 * both parse successfully, so a regex or a zod `.url()` check is insufficient
 * to keep a `javascript:` payload out of an anchor href — we must inspect the
 * parsed protocol.
 */
export function isSafeHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Returns the URL to use in an anchor `href`, or `null` when it is not a safe
 * http(s) URL. Callers render plain text (never a clickable anchor) for the
 * null case, which closes the stored-XSS path at the render sink for both new
 * form input and already-stored legacy data.
 */
export function safeExternalHref(url: string): string | null {
  return isSafeHttpUrl(url.trim()) ? url : null;
}

export interface CleanedEvidenceLinks {
  /** Trimmed, validated links to persist (empty when `error` is set). */
  kept: EvidenceLink[];
  /** Friendly inline error, or null when every kept row is valid. */
  error: string | null;
}

/**
 * Trim both fields, drop untouched-default / fully-blank rows, validate the
 * rest. Drop rule: a row is dropped only when BOTH fields are blank, OR its
 * label equals {@link DEFAULT_EVIDENCE_LABEL} and its URL is blank (an
 * untouched seed). Any other row — a user-labelled row missing a URL, a URL
 * with no label, or a non-http(s) URL — is treated as user intent and yields
 * a friendly error rather than being silently dropped, so intent is not lost.
 */
export function cleanEvidenceLinks(links: EvidenceLink[]): CleanedEvidenceLinks {
  const kept: EvidenceLink[] = [];
  for (const link of links) {
    const label = link.label.trim();
    const url = link.url.trim();
    const bothBlank = label === "" && url === "";
    const untouchedDefault = label === DEFAULT_EVIDENCE_LABEL && url === "";
    if (bothBlank || untouchedDefault) continue;
    if (label === "" || url === "" || !isSafeHttpUrl(url)) {
      return { kept: [], error: EVIDENCE_LINK_ERROR };
    }
    kept.push({ label, url });
  }
  return { kept, error: null };
}
